/**
 * メール転送の同意確認（**認証不要**の着地ページ）。
 *
 * `POST /api/v1/portal/forwarders` が転送先へ送る確認メールのリンクは
 * `${PORTAL_ORIGIN}/forwards/verify?token=...`（`routes/portal/aliases.ts`）だが、
 * **SPA にそのパスが無く 404 になっていた**。転送先の同意が取れないので、
 * 転送は永久に `verified=false` のまま配送に効かない——つまり
 * 「転送を設定したのに 1 通も届かない」が仕様どおりに起きていた。
 *
 * CP 側には JS 不要の受け口（`GET /api/v1/public/forwards/verify`）もあるが、
 * メールのリンクが指しているのはこちらなので、ここで受けて
 * `POST /api/v1/public/forwards/verify` に `{ token }` を送る。
 *
 * 守っていること:
 *   1. **開いただけでは有効化しない。** 確認メールはメールクライアントや
 *      セキュリティ製品にリンクを先読みされることがあり、GET で有効化すると
 *      本人が読む前に同意したことになる。必ず本人のクリック（POST）で確定する。
 *   2. トークンは URL からだけ読み、画面の入力値として持たない。
 *   3. ログインを要求しない。転送先は契約者ユーザーとは限らない
 *      （そもそもこの製品のアカウントを持っていないことの方が多い）。
 */
import * as React from 'react';
import { useMutation } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@mail-sv/ui';
import { CheckCircle2, Forward } from 'lucide-react';
import { publicApi, errorMessage } from '../lib/api.js';
import { useSearchParams } from '../lib/nav.js';
import { env } from '../env.js';

/** `POST /api/v1/public/forwards/verify` の応答。 */
interface ForwardVerifyResult {
  ok: boolean;
  verified: boolean;
}

export function ForwardVerifyPage() {
  const search = useSearchParams();
  const token = typeof search.token === 'string' ? search.token : '';

  const confirm = useMutation({
    mutationFn: () => publicApi.post<ForwardVerifyResult>('/forwards/verify', { token }),
  });

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4 py-10">
      <div className="w-full max-w-md space-y-6">
        <div className="space-y-2 text-center">
          <div className="brand-gradient mx-auto flex size-12 items-center justify-center rounded-xl text-white shadow-lg shadow-primary/25">
            <Forward className="size-6" aria-hidden />
          </div>
          <h1 className="text-xl font-semibold">{env.brandName}</h1>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>メール転送の確認</CardTitle>
            <CardDescription>
              このメールアドレス宛に、別のメールアドレスからの転送を設定しようとしています。
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {confirm.isSuccess ? (
              <>
                <Alert variant="success">
                  <AlertTitle className="flex items-center gap-2">
                    <CheckCircle2 className="size-4" aria-hidden />
                    転送を有効にしました
                  </AlertTitle>
                  <AlertDescription>
                    これ以降、転送元に届いたメールがこのアドレスにも届きます。
                    転送を止めたい場合は、設定した方にご連絡ください。
                  </AlertDescription>
                </Alert>
                <p className="text-xs text-muted-foreground">
                  この画面は閉じていただいて構いません。
                </p>
              </>
            ) : token.length === 0 ? (
              <Alert variant="warning">
                <AlertTitle>リンクが正しくありません</AlertTitle>
                <AlertDescription>
                  メール内のリンクをそのまま開いてください。有効期限が切れている場合は、
                  転送を設定した方に再度お手続きいただく必要があります。
                </AlertDescription>
              </Alert>
            ) : (
              <>
                <Alert variant="info">
                  <AlertTitle>心当たりがない場合は、そのまま閉じてください</AlertTitle>
                  <AlertDescription>
                    下のボタンを押さなければ、転送は有効になりません。
                    身に覚えのない転送設定の場合は、何も操作せずにこの画面を閉じてください。
                  </AlertDescription>
                </Alert>

                {confirm.isError ? (
                  <Alert variant="destructive">
                    <AlertTitle>確認できませんでした</AlertTitle>
                    <AlertDescription>{errorMessage(confirm.error)}</AlertDescription>
                  </Alert>
                ) : null}

                <Button
                  className="w-full"
                  onClick={() => confirm.mutate()}
                  disabled={confirm.isPending}
                >
                  {confirm.isPending ? '確認しています…' : 'このメール転送を許可する'}
                </Button>
              </>
            )}
          </CardContent>
        </Card>

        <p className="text-center text-sm text-muted-foreground">
          <Link to="/legal" className="underline">
            規約・法令表示
          </Link>
        </p>
      </div>
    </div>
  );
}
